import { AbstractControl, ValidatorFn } from '@angular/forms';

export class SpecialFormValidators {
    private static IbanCodes = {
        AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
        CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
        FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
        HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
        LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
        MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
        RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26
    };
    /**
     * Validator that requires controls to have a non-empty value.
     */
    public static validateNIF(control: AbstractControl): { [key: string]: boolean } {
        let nif: string = control.value;

        if (!nif || nif == '') {
            return null;
        }

        if (nif.length < 8) {
            return SpecialFormValidators.getNifError();
         }
        const initialLetter = nif.substr(0, 1).toUpperCase();
        if (SpecialFormValidators.isLetter(initialLetter)) {
            switch (initialLetter) {
                case 'X':
                    nif = '0' + nif.substr(1, nif.length - 1);
                    return SpecialFormValidators.validarNIF(nif);
                case 'Y':
                    nif = '1' + nif.substr(1, nif.length - 1);
                    return SpecialFormValidators.validarNIF(nif);
                case 'Z':
                    nif = '2' + nif.substr(1, nif.length - 1);
                    return SpecialFormValidators.validarNIF(nif);
            }
        } else if (SpecialFormValidators.isLetter(nif.substr(-1))) {
            if (new RegExp('[0-9]{8}[A-Za-z]').exec(nif) || new RegExp('[0-9]{7}[A-Za-z]').exec(nif)) {
                return SpecialFormValidators.validarNIF(nif);
             }
        }
        return SpecialFormValidators.getNifError();
    }

    public static validateCIF(control: AbstractControl): { [key: string]: boolean } {
        let cif: string = control.value;

        if (!cif || cif == '') {
            return null;
        }

        if (!cif || cif == '' || cif.replace(' ', '').length < 8) {
            return SpecialFormValidators.getCifError();
        }
        cif = cif.replace(' ', '');
        const initialLetter = cif.substr(0, 1).toUpperCase();
        if (SpecialFormValidators.isLetter(initialLetter) &&
            new RegExp('[A-Za-z][0-9]{7}[A-Za-z0-9]{1}$').exec(cif)) {
            return SpecialFormValidators.validarCIF(cif);
        }

        return SpecialFormValidators.getCifError();
    }


    public static validateCUPS(control: AbstractControl): { [key: string]: boolean } {
        let cups: string = control.value;
        if (cups) {
            cups = cups.replace(' ', '');
        }
        if (!cups || cups == '') {
            return null;
        }

        if (!SpecialFormValidators.isCorrectCupsLength(cups) ||
            !(cups.substr(0, 2) == 'ES') ||
            !SpecialFormValidators.isCorrectCupsFormat(cups)) {
            return SpecialFormValidators.getCupsError();
        }

        const sCups = cups.substr(2, 16);
        const sControl = cups.substr(18, 2);

        if (isNaN(Number(sCups.substr(0, 16)))) {
            return SpecialFormValidators.getCupsError();
        }
        let nVerificacion = Number(sCups.substr(0, 16));
        nVerificacion %= 529;

        const nVerificacion1 = Math.floor(nVerificacion / 23);
        const nVerificacion2 = nVerificacion % 23;

        const sCalculado = SpecialFormValidators.getLetra(nVerificacion1) + SpecialFormValidators.getLetra(nVerificacion2);

        return sControl != sCalculado ? SpecialFormValidators.getCupsError()
                                     : null;
    }

    public static validateIBAN(control: AbstractControl): { [key: string]: boolean } {
        let iban: string = control.value;
        if (!iban) { return null; }

        iban = iban.replace(' ', '').toUpperCase();

        if (iban == '') { return null; }

        iban = iban.replace(/[^A-Z0-9]/g, ''); // keep only alphanumeric characters
        const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/); // match and capture (1) the country code, (2) the check digits, and (3) the rest
        // check syntax and length
        if (!code || iban.length !== SpecialFormValidators.IbanCodes[code[1]]) {
            return SpecialFormValidators.getIBANError();
        }
        // rearrange country code and check digits, and convert chars to ints
        const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, function (letter) {
            return (letter.charCodeAt(0) - 55).toString();
        });
        // final check
        return SpecialFormValidators.mod97(digits) != 1 ? SpecialFormValidators.getIBANError()
                                                        : null;
    }

    public static validateMovilNumber(control: AbstractControl): { [key: string]: boolean } {
        let number: string = control.value;

        if (!number || number == '') {
            return null;
        }
        number = number.replace(' ', '');
        if (number.length != 9
            || !SpecialFormValidators.isNumeric(number)
            || parseInt(number.substring(0, 1), 10) < 6) {
            return SpecialFormValidators.getMovilNumberError();
        }

        return null;
    }

    public static validateAuthenticationCode(control: AbstractControl): { [key: string]: boolean } {
        const number: string = control.value;

        if (!number || number == '') {
            return null;
        }
        if (number.length != 6
            || !SpecialFormValidators.isNumeric(number)) {
            return SpecialFormValidators.getAuthenticationCodeError();
        }

        return null;
    }

    private static isNumeric(n) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }

    private static mod97(digits: string) {
        let checksum = Number(digits.slice(0, 2));
        for (let offset = 2; offset < digits.length; offset += 7) {
            const fragment = String(checksum) + digits.substring(offset, offset + 7);
            checksum = parseInt(fragment, 10) % 97;
        }

        return checksum;
    }

    private static isCorrectCupsFormat(cups: string) {
        return !(cups.length == 22 && cups.substr(20, 2) != '1P' && cups.substr(20, 2) != '0F');
    }

    private static isCorrectCupsLength(cups: string) {
        return (cups.length == 20 || cups.length == 22);
     }


    private static getCifError(): { [key: string]: boolean; } {
        return { 'invalidCIF': true };
    }
    private static getNifError(): { [key: string]: boolean; } {
        return { 'invalidNIF': true };
    }

    private static getCupsError(): { [key: string]: boolean; } {
        return { 'invalidCUPS': true };
    }

    private static getIBANError(): { [key: string]: boolean; } {
        return { 'invalidIBAN': true };
    }

    private static getMovilNumberError(): { [key: string]: boolean; } {
        return { 'invalidMovilNumber': true };
    }

    private static getAuthenticationCodeError(): { [key: string]: boolean; } {
        return { 'invalidAuthenticationCode': true };
    }

    private static isLetter(str) {
        return str.length === 1 && str.match(/[a-z]/i);
    }

    private static validarNIF(nif: string): { [key: string]: boolean } {
        if (!nif || nif == '') {
            return SpecialFormValidators.getNifError();
         }
        const letra: string = nif.substr(-1);
        nif = nif.substr(0, nif.length - 1);
        const nifNum: number = parseInt(nif, 10);
        const resto = nifNum % 23;
        const tmp: string = SpecialFormValidators.getLetra(resto);

        return tmp.toLowerCase() != letra.toLowerCase() ? SpecialFormValidators.getNifError()
                                                        : null;
    }

    private static getLetra(id: number): string {
        const letras = ['T', 'R', 'W', 'A', 'G', 'M', 'Y', 'F', 'P', 'D', 'X', 'B', 'N', 'J', 'Z', 'S', 'Q', 'V', 'H', 'L', 'C', 'K', 'E'];
        return letras[id];
    }

    private static add(num1: number, num2: number, currentIndex: number, array: number[]): number {
        return Number(num1) + Number(num2);
    }

    private static calculateOddValueCIF(accumulator: string): Number {
        const numbers: number[] = (Number(accumulator) * 2).toString().split('').map(num => Number(num));
        return numbers.reduce(SpecialFormValidators.add);
    }

    private static validarCIF(cif: string): { [key: string]: boolean } {
        const validLetters = 'ABCDEFGHJNPQRSUVW';

        const entityKey = cif.substr(0, 1).toUpperCase();
        const digitControlKey = cif.substr(-1).toUpperCase();


        if (validLetters.indexOf(entityKey) == -1) { return SpecialFormValidators.getCifError(); }

        const digitsWithoutDigitControl: string[] = cif.substr(1, 7).split('');

        const digitCouples: Number[] = [Number(digitsWithoutDigitControl[1]),
                                        Number(digitsWithoutDigitControl[3]),
                                        Number(digitsWithoutDigitControl[5])];
        const digitOdds: Number[] = [
            SpecialFormValidators.calculateOddValueCIF(digitsWithoutDigitControl[0]),
            SpecialFormValidators.calculateOddValueCIF(digitsWithoutDigitControl[2]),
            SpecialFormValidators.calculateOddValueCIF(digitsWithoutDigitControl[4]),
            SpecialFormValidators.calculateOddValueCIF(digitsWithoutDigitControl[6])];

        const digitCouplesSum: number = Number(digitCouples.reduce(SpecialFormValidators.add));

        const digitOddSum: number = Number(digitOdds.reduce(SpecialFormValidators.add));

        let digitControl = Number((digitCouplesSum + digitOddSum).toString().substr(-1));

        digitControl = digitControl == 0 ? digitControl : 10 - digitControl;

        const arrayLettersControl = 'JABCDEFGHI';

        if ('PQRSW'.indexOf(entityKey) != -1) {
            return digitControlKey != arrayLettersControl[digitControl] ? this.getCifError()
                                                                        : null;
        }
        if ('ABEH'.indexOf(entityKey) != -1) {
            return digitControlKey != digitControl.toString() ? this.getCifError()
                                                              : null;
        }

        return digitControlKey != arrayLettersControl[digitControl] && digitControlKey != digitControl.toString()
            ? this.getCifError() : null;
    }
}
